// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#030712] preserve le rendu dark historique (voir memoire ui-themes)
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, BookOpen } from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { PublicFooter } from "../components/PublicFooter";
import { getGuideList } from "./guidesData";
import { SEOHead } from "../components/SEOHead";
import { localizedPath } from "../seo/enRoutes";

const CATEGORIES: { labelKey: string; guides: string[] }[] = [
  {
    labelKey: "guides_category_quick_start",
    guides: ["fonctionnalites-avancees"],
  },
  {
    labelKey: "guides_category_work_team",
    guides: ["planifier-reunion-equipe", "planifier-reunion-parents"],
  },
  {
    labelKey: "guides_category_sport_club",
    guides: [
      "planifier-club-sportif-culturel",
      "planifier-entrainements-equipe-foot",
      "organiser-tournoi-sport",
      "planning-equipe-sport-saisonnier",
    ],
  },
  {
    labelKey: "guides_category_music_arts",
    guides: [
      "planifier-repetitions-chorale",
      "planifier-repetitions-theatre",
      "planifier-cours-danse-musique",
    ],
  },
  {
    labelKey: "guides_category_associations",
    guides: [
      "assemblee-generale-association",
      "sondage-disponibilites-benevoles",
    ],
  },
  {
    labelKey: "guides_category_travel_events",
    guides: [
      "planifier-voyage-groupe",
      "organiser-soiree-amis",
      "sondage-date-mariage",
      "organiser-anniversaire-groupe",
    ],
  },
];

export default function GuidesIndex() {
  const { t, i18n } = useTranslation(["polls", "common", "errors"]);
  const { pathname } = useLocation();
  const guideMap = Object.fromEntries(
    getGuideList(i18n.language).map((g) => [g.slug, g]),
  );
  return (
    <div className="flex min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
      <SEOHead
        title={t("polls:guides_seo_title")}
        description={t("polls:guides_seo_description")}
      />
      <ProductSidebar productType="date" />
      <div className="flex-1 min-w-0 flex flex-col">
        <ProductHeader productType="date" showAuth={true} />

        <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-16">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-2 text-primary text-sm mb-4">
              <BookOpen className="w-4 h-4" />
              <span>{t("polls:guides_badge")}</span>
            </div>
            <h1 className="text-4xl font-bold mb-4">
              {t("polls:guides_title")}
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl">
              {t("polls:guides_subtitle")}
            </p>
          </div>

          {/* Guides par catégorie */}
          <div className="space-y-12">
            {CATEGORIES.map((cat) => {
              const guides = cat.guides
                .map((slug) => guideMap[slug])
                .filter(Boolean);
              if (!guides.length) return null;
              return (
                <section key={cat.labelKey}>
                  <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                    {t(`polls:${cat.labelKey}`)}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {guides.map((g) => (
                      <Link
                        key={g.slug}
                        to={localizedPath(pathname, `/guides/${g.slug}`)}
                        className="group flex items-center justify-between p-5 rounded-xl border border-border bg-card hover:bg-accent hover:border-border/80 transition-all"
                      >
                        <span className="font-medium text-foreground group-hover:text-primary transition-colors">
                          {g.title}
                        </span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0 ml-4" />
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          {/* CTA bas de page */}
          <div className="mt-16 p-8 rounded-2xl bg-primary/10 border border-primary/20 text-center">
            <p className="text-lg font-semibold mb-2">
              {t("polls:guides_cta_title")}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              {t("polls:guides_cta_subtitle")}
            </p>
            <Link
              to="/create"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-xl transition-all shadow-lg shadow-primary/20"
            >
              {t("polls:guides_cta_button")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </main>

        <PublicFooter />
      </div>
    </div>
  );
}
